import { Injectable } from '@angular/core';
import { Apollo, gql, TypedDocumentNode } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OccupationDaoModel } from '@core/dao/occupation.dao.model';
import {
  AutocompleteOccupations,
  NationalOccupationSalary,
  OccupationByOnetCode,
  OccupationsByCipCode
} from '@core/api/queries/occupation-graph-queries';

export interface NationalOccupationSalaryModel {
  medianAnnualSalary: number;
  minimumAnnualSalary: number;
  maximumAnnualSalary: number;
}

@Injectable()
export class OccupationGraphqlApi {
  private readonly name = 'occupationGraphql';

  public constructor(private apollo: Apollo) {}

  public query<T>(
    query: TypedDocumentNode<unknown, unknown>,
    variables: Record<string, unknown>,
    options?: Record<string, unknown>
  ): Observable<T> {
    return this.apollo
      .use(this.name)
      .query({
        query,
        variables,
        ...options
      })
      .pipe(map(res => res.data as T));
  }

  public getAutocompleteOccupations(
    keyword: string,
    options?: Record<string, unknown>
  ): Observable<OptionItemDaoModel[]> {
    return this.query<{
      autocompleteOccupations: OptionItemDaoModel[];
    }>(AutocompleteOccupations, { keyword }, options).pipe(map(value => value.autocompleteOccupations || []));
  }

  public getOccupationByOnetCode(onetCode: string, options?: Record<string, unknown>): Observable<OccupationDaoModel> {
    return this.query<{
      occupationByOnetCode: OccupationDaoModel;
    }>(OccupationByOnetCode, { onetCode }, options).pipe(map(value => value.occupationByOnetCode));
  }

  /**
   * National pay for an occupation, or null. Anything not labelled national is rejected, so a change
   * in what the service returns cannot quietly put a small-area estimate in front of a user.
   */
  public getNationalOccupationSalary(
    onetCode: string,
    options?: Record<string, unknown>
  ): Observable<NationalOccupationSalaryModel | null> {
    return this.query<{
      occupationByOnetCode: {
        salaryAnalysis: (NationalOccupationSalaryModel & { salaryPrecisionType: string }) | null;
      } | null;
    }>(NationalOccupationSalary, { onetCode }, options).pipe(
      map(value => {
        const salary = value.occupationByOnetCode?.salaryAnalysis;
        const isNational = !!salary?.salaryPrecisionType?.startsWith('National');
        return salary && isNational && salary.medianAnnualSalary > 0 ? salary : null;
      })
    );
  }

  public getOnetCodesByCipCode(
    cipCode: string,
    options?: Record<string, unknown>
  ): Observable<
    {
      onetCode: string;
      primaryIndustryId: number;
    }[]
  > {
    return this.query<{
      occupationListByCipCode: { onetCode: string; primaryIndustryId: number }[];
    }>(OccupationsByCipCode, { cipCode }, options).pipe(map(value => value.occupationListByCipCode));
  }
}
